import {Injectable} from '@angular/core';
import {
    CommandResult,
    FilterOutputModel,
    FiltersService,
    GeneralService,
    HttpService,
    SettingsProvider,
    TableResponseQuery
} from 'app/core';
import {TableHelperService} from 'app/shared/tables/table-helper.service';
import {Observable} from 'rxjs';
import {environment as env} from '../../../../environments/environment';
import {HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {FeaturesListQuery} from 'app/domain/features/features-model';
import {CurrentAssortmentFeaturesDetailsListModel} from 'app/prices/current-assortment/models/current-assortment-features-details-list.model';

export interface FeatureCreateCommand {
    name: string;
    type: string;
    possibleValues?: string[];
    updatedAt: string;
    updatedByUserName: string;
    createdByUserName: string;
    id: number;
}

export interface FeatureUpdateCommand {
    name: string;
    type: string;
    id: number;
    possibleValues?: string[];
    updatedAt: string;
    updatedByUserName: string;
    createdByUserName: string;
}

export interface FeatureSetCommand {
    productAttributesList: ProductAttribute[];
}

export interface ProductAttribute {
    productId: number;
    attributesList: {
        attributeId: number,
        value: string | boolean
    }[];
}

export interface Attribute {
    AttributeId: number;
    Value: string;
}

export interface AttributeWithId {
    Id: number;
    AttributeId: number;
    Value: string | boolean;
}

export interface Feature {
    ProductId: number;
    ProductName: string;
    AttributesList: Attribute[];
}

@Injectable()
export class FeaturesDataService {

    private features: FeatureCreateCommand[];

    constructor(
        private http: HttpService,
        private tableHelperService: TableHelperService,
        public filterService: FiltersService,
        public settingsService: SettingsProvider,
        private generalService: GeneralService
    ) {
    }

    fetchData(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[],
        parseFilter = true
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlGetList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (!parseFilter && filter !== '') {
            params = params.set('$filter', filter);
        } else if (filter || (selectFilters && !!selectFilters.length)) {
            const columns = FeaturesListQuery.tableInfo().columns;

            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    columns
                )
            );

            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = response.body.items;
                this.features = response.body.items;
                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
        );
    }

    fetchFeatures(
        top: number = 100,
        skip: number = 0,
        sortBy: string = '',
        filter: string = '',
        selectFilters: FilterOutputModel[]
    ): Observable<TableResponseQuery> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlGetProductsList}`;
        let params = new HttpParams();
        params = params.set('$count', 'true');

        params = top ? params.set('$top', top.toString()) : params;
        params = skip ? params.set('$skip', skip.toString()) : params;
        params = sortBy ? params.set('$orderby', sortBy.toString()) : params;

        // logic to set filter values
        if (filter || (selectFilters && !!selectFilters.length)) {
            const columns = CurrentAssortmentFeaturesDetailsListModel.tableInfo().columns;

            const mainFilterString = this.filterService.setFilters(
                filter,
                selectFilters,
                this.tableHelperService.buildSearchUrl(
                    filter,
                    columns
                ),
                this.filterService.setSelectFiltersForList(
                    selectFilters,
                    columns
                )
            );

            params = params.set('$filter', mainFilterString);
        }

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                const items = response.body.items;

                return new TableResponseQuery(
                    items,
                    response.body.count
                );
            }),
            map((response) => {
                const newItems = response.items.map((item) => {
                    const newItem = {
                        ...item,
                        CategoryName: this.generalService.getCategoryNameLevel(item, 'categoryNameLevel')
                    };
                    newItem.attributesList.forEach(attribute => {
                        newItem[`feature${attribute.attributeId}`] = attribute.value;
                    });

                    return newItem;
                });
                return {
                    total_count: response.total_count,
                    items: newItems
                };
            })
        );
    }

    getById(id: number): Observable<FeatureUpdateCommand> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlGetList}`;

        let params = new HttpParams();
        params = id ? params.set('$filter', `id eq ${id.toString()}`) : params;

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                return response.body.items[0];
            }),
        );
    }

    getFeaturesDef(): FeatureCreateCommand[] {
        return this.features;
    }

    getFeatureById(id: number): Observable<Feature> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlGetProductsList}`;

        let params = new HttpParams();
        params = id ? params.set('$filter', `productId eq ${id.toString()}`) : params;

        return this.http.getWithParams(url, params).pipe(
            map((response) => {
                return this.generalService.toUpperCamelCaseObj(response.body.items)[0];
            }),
            map((response) => {
                response.AttributesList.forEach(attribute => {
                    if (attribute.Value === 'true') {
                        attribute.Value = true;
                    }
                    if (attribute.Value === 'false') {
                        attribute.Value = false;
                    }
                });
                return response;
            }),
        );
    }

    setFeatures(command: FeatureSetCommand): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlSetProducts}`;
        return this.http.post(url, command);
    }

    create(command: FeatureCreateCommand): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlCreate}`;
        return this.http.post(url, command);
    }

    update(command: FeatureUpdateCommand): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlUpdate}`;
        return this.http.put(url, command);
    }

    delete(id: number): Observable<CommandResult> {
        const url = `${this.settingsService.configuration.url}${env.FeaturesDataService.apiUrlDelete}/${id}`;
        return this.http.delete(url);
    }
}
